/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, mixins } from '@blend/consumer-components';
import { MaterialIcon } from '@blend/enterprise-components';
import SigningDocument from '../../common/Documents/SigningDocument';
import { DocumentStatusTag, getTagAttributes } from '../../common/DocumentStatusTag/DocumentStatusTag';
import { DocumentStatus } from '@shared/constants/document';

export namespace Document {
  export interface Props {
    isTrash: boolean;
    isSelected: boolean;
    document: SigningDocument;
    handleClick: (document: SigningDocument) => void;
  }
}

const buttonWidth = 24;

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: colors.catalyst,
    backgroundColor: colors.white,
    width: '100%',
    ...mixins.padding(12, 8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
  },
  selectedWrapper: {
    backgroundColor: colors.reflexLight08,
  },
  buttonWrapper: {
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: `calc(100% - ${buttonWidth}px)`,
    ...mixins.padding(0),
  },
  name: {
    fontWeight: 'bold',
    fontSize: 16,
    lineHeight: '22px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },
  statusWrapper: {
    width: '100%',
    display: 'flex',
  },
  status: {
    fontSize: 12,
    color: colors.catalyst,
    lineHeight: '16px',
    ...mixins.marginVertical(3),
    ...mixins.marginHorizontal(8),
  },
  statusDot: {
    ...mixins.marginVertical(6),
    display: 'flex',
    borderRadius: '50%',
    width: 8,
    height: 8,
  },
  tagged: {
    backgroundColor: colors.mint,
  },
  pending: {
    backgroundColor: colors.orange,
  },
  dragIndicator: {
    display: 'flex',
    alignItems: 'center',
    ...mixins.margin(0, 5, 0, 0),
  },
});

export const Document: React.FC<Document.Props> = ({ isTrash, isSelected, document, handleClick }) => {
  const { name, lastTaggedAt, status } = document;

  const statusText = lastTaggedAt ? 'Tagged' : 'Pending tags';
  const statusTagAttributes = getTagAttributes(status, false, false, undefined, undefined);

  const isTerminalDoc = [DocumentStatus.COMPLETED, DocumentStatus.VOIDED].includes(status);
  let statusElm;
  if (isTerminalDoc && statusTagAttributes) {
    statusElm = (
      <DocumentStatusTag
        indicatorType={statusTagAttributes.indicatorType}
        indicatorText={statusTagAttributes.indicatorText}
      />
    );
  } else {
    statusElm = (
      <>
        <div className={css(styles.statusDot, isTerminalDoc || lastTaggedAt ? styles.tagged : styles.pending)} />
        <div className={css(styles.status)}>{statusText}</div>
      </>
    );
  }

  return (
    <div
      className={css(styles.wrapper, isSelected && styles.selectedWrapper)}
      data-it={isTrash ? `Trash_Document_${document.id}` : `Document_${document.id}`}
    >
      <div className={css(styles.dragIndicator)}>
        {!isTrash && !isTerminalDoc && (
          <MaterialIcon type="drag_indicator" size={24} color={colors.catalystLight40}></MaterialIcon>
        )}
      </div>
      <button className={css(styles.buttonWrapper)} onClick={() => handleClick(document)} data-it="document-button">
        <div className={css(styles.name)} data-it="document-name">
          {name}
        </div>
        <span className={css(styles.statusWrapper)} data-it="document-status">
          {statusElm}
        </span>
      </button>
    </div>
  );
};
